import { Chart, Plugin } from "chart.js";

import { CHART_SENTIMENT_COLORS } from "@/utils";

type GradientStop = { at: number; color: string };

type GradientPluginOptions = {
  stops: GradientStop[];
  axis: "x" | "y";
};

/**
 * A gradient plugin that sets the color of the line to a gradient along an axis. the default is Y.
 * Accepts a list of stops that are objects with a color and a position.
 */
const GradientPlugin: Plugin<"line"> = {
  id: "gradient",
  beforeDatasetsUpdate(chart: Chart<"line">, _args, options) {
    if (!chart) {
      return;
    }
    const { stops, axis } = options as GradientPluginOptions;
    const { ctx, chartArea } = chart;
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    let width = 0;
    let height = 0;
    if (width !== chartWidth || height !== chartHeight) {
      // Create the gradient because this is either the first render
      // or the size of the chart has changed
      width = chartWidth;
      height = chartHeight;
      // create a gradient with the dimensions of the chart
      const gradient = ctx.createLinearGradient(
        axis === "x" ? chartArea.left : 0,
        axis === "y" ? chartArea.bottom : 0,
        axis === "x" ? chartArea.right : 0,
        axis === "y" ? chartArea.top : 0
      );
      stops.forEach(({ color, at }) => {
        gradient!.addColorStop(at, color);
      });
      chart.data.datasets?.forEach((dataset) => {
        if (dataset) {
          dataset.borderColor = gradient;
          dataset.pointBackgroundColor = gradient;
          dataset.pointBorderColor = gradient;
          dataset.pointHoverBackgroundColor = gradient;
          dataset.pointHoverBorderColor = gradient;
        }
      });
    }
  },

  defaults: {
    axis: "y",
    stops: [
      { color: CHART_SENTIMENT_COLORS.negative, at: 0.25 },
      { color: CHART_SENTIMENT_COLORS.neutral, at: 0.5 },
      { color: CHART_SENTIMENT_COLORS.positive, at: 0.75 },
    ],
  },
};

export default GradientPlugin;
